import { ThemeEvents } from '@theme/events';

const ADDED_FEEDBACK_DURATION = 2000;

class StickyAtcBar extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #addedTimeout;

  /** Whether the current variant is available for purchase */
  #isAvailable = true;

  /** Whether the original ATC button is out of view */
  #isOutOfView = false;

  connectedCallback() {
    const section = this.closest('.shopify-section');
    if (!section) return;

    const { signal } = this.#abortController;

    // Find the original ATC button within the same section
    const originalButton = section.querySelector('add-to-cart-component [ref="addToCartButton"]');

    if (originalButton) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            this.#isOutOfView = !entry.isIntersecting;
            this.#updateVisibility();
          }
        },
        { threshold: 0 }
      );
      this.#observer.observe(originalButton);
    }

    // Listen for variant updates to sync button state
    section.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });

    // Listen for cart updates to show "Added" feedback
    section.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  /**
   * Determine whether the sticky bar should be visible based on
   * scroll position (observer) and variant availability.
   */
  #updateVisibility() {
    const visible = this.#isOutOfView && this.#isAvailable;
    this.classList.toggle('sticky-atc-bar--visible', visible);
    this.setAttribute('aria-hidden', String(!visible));
    this.#shiftChatWidget(visible);
  }

  /**
   * Nudge the LiveChat widget up/down so it doesn't overlap the sticky bar.
   * @param {boolean} visible - Whether the sticky bar is visible.
   */
  #shiftChatWidget(visible) {
    const widget = document.getElementById('chat-widget-container');
    if (!widget) return;
    const barHeight = visible ? this.offsetHeight : 0;
    widget.style.transition = 'transform 0.3s ease';
    widget.style.transform = visible ? `translateY(-${barHeight}px)` : '';
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#abortController.abort();
    this.#shiftChatWidget(false);

    if (this.#addedTimeout) {
      clearTimeout(this.#addedTimeout);
    }
  }

  /**
   * @param {import('@theme/events').VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    const button = this.querySelector('.sticky-atc-bar__button');
    if (!button) return;

    const resource = event.detail.resource;
    this.#isAvailable = resource != null && resource.available !== false;

    // Update disabled state
    button.disabled = !this.#isAvailable;

    // Update button text and icon from the HTML response
    const newAtcButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');
    if (newAtcButton) {
      const newText = newAtcButton.querySelector('.add-to-cart-text');
      const currentText = button.querySelector('.add-to-cart-text');
      if (newText && currentText) {
        currentText.innerHTML = newText.innerHTML;
      }
    }

    // Re-evaluate visibility with new availability state
    this.#updateVisibility();
  };

  #onCartUpdate = (event) => {
    // Only show feedback for successful adds from the product form
    if (event.detail?.data?.didError) return;
    if (event.detail?.data?.source !== 'product-form-component') return;

    const button = this.querySelector('.sticky-atc-bar__button');
    if (!button) return;

    if (this.#addedTimeout) clearTimeout(this.#addedTimeout);

    button.classList.add('atc-added');

    this.#addedTimeout = setTimeout(() => {
      button.classList.remove('atc-added');
    }, ADDED_FEEDBACK_DURATION);
  };
}

if (!customElements.get('sticky-atc-bar')) {
  customElements.define('sticky-atc-bar', StickyAtcBar);
}